import { Chip, Radio } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"

import { PRODUCT_STOCK_TYPE_COLOR_MAP, PRODUCT_STOCK_TYPE_LABEL_MAP } from "../constants"

import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import { SELECT_COLUMN_SIZE } from "@/constants/common"
import type { Product } from "@/graphql/codegen/graphql"
import { sortEmptyLast } from "@/utils/table"

interface UseAssociateProductColumnsProps {
  showRadioButton: boolean
  disabledProductIds?: number[]
  onSelect?: (product: Product) => void
}

const useAssociateProductColumns = ({
  showRadioButton,
  disabledProductIds = [],
  onSelect,
}: UseAssociateProductColumnsProps): ColumnDef<Product>[] => {
  const columns: ColumnDef<Product>[] = []

  if (showRadioButton) {
    columns.push({
      id: "select",
      header: "",
      cell: ({ row }) => (
        <Radio
          checked={row.getIsSelected()}
          disabled={disabledProductIds.includes(row.original.productId)}
          onClick={() => onSelect?.(row.original)}
          readOnly
          data-testid={`associate-product-radio-${row.original.productId}`}
        />
      ),
      size: SELECT_COLUMN_SIZE,
    })
  }

  columns.push(
    {
      id: "productId",
      header: "Product ID",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.publicId ?? "",
      cell: ({ row }) => (
        <ProductStatusCell product={row.original} data-testid={`product-status-${row.original.productId}`} />
      ),
    },
    {
      accessorKey: "companyProductDescription",
      header: "Akrochem Desc",
      enableSorting: true,
      cell: ({ row }) => row.original.companyProductDescription ?? "-",
    },
    {
      accessorKey: "vendorDescription",
      header: "Supplier Desc",
      enableSorting: true,
      cell: ({ row }) => row.original.vendorDescription ?? "-",
    },
    {
      id: "vendor",
      header: "Supplier",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.company?.name ?? "",
      cell: ({ row }) => row.original.company?.name ?? "-",
    },
    {
      id: "shipFrom",
      header: "Ship from",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => row.locationsAssociations?.[0]?.shipping?.name ?? "",
      cell: ({ row }) => row.original.locationsAssociations?.[0]?.shipping?.name ?? "-",
    },
    {
      id: "stockStatus",
      header: "Stock Status",
      enableSorting: true,
      sortingFn: sortEmptyLast,
      accessorFn: (row) => (row.stockType ? PRODUCT_STOCK_TYPE_LABEL_MAP[row.stockType] : ""),
      cell: ({ row }) => {
        const { stockType } = row.original
        if (!stockType) return "-"
        const label = PRODUCT_STOCK_TYPE_LABEL_MAP[stockType]
        const color = PRODUCT_STOCK_TYPE_COLOR_MAP.get(stockType)
        return <Chip label={label} color={color as "error" | "warning" | "success" | undefined} size='small' />
      },
    }
  )

  return columns
}

export default useAssociateProductColumns
